import { Link, router } from "@inertiajs/react";
import { ArrowLeft, Phone, Mail } from "lucide-react";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { FichaBody } from "@/components/ficha/FichaBody";
import { staffMetrics } from "@/components/ficha/metrics";
import type { BookingSummary, PageProps, StaffMember } from "@/types";

interface Props extends PageProps {
  staff_member: StaffMember;
  analytics: BookingSummary;
  can_edit?: boolean;
  can_delete?: boolean;
}

export default function TeamShow({
  staff_member: member,
  analytics,
  can_edit = false,
  can_delete = false,
  current_user,
  current_organization,
  current_business,
}: Props) {
  const orgSlug = current_organization?.slug || "";

  const handleDelete = () => {
    if (!confirm(`¿Eliminar a "${member.name}" del equipo?`)) return;
    router.delete(`/${orgSlug}/team/${member.id}`);
  };

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="space-y-6">
        <div>
          <Link
            href={`/${orgSlug}/team`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a equipo
          </Link>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold">{member.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {member.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" />
                    {member.phone}
                  </span>
                )}
                {member.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    {member.email}
                  </span>
                )}
                <span>{member.role === "admin" ? "Administrador" : "Personal"}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {!member.pending && (
                <Link href={`/${orgSlug}/team/${member.id}/availability`}>
                  <Button variant="outline">Horarios</Button>
                </Link>
              )}
              {can_edit && !member.pending && (
                <Link href={`/${orgSlug}/team/${member.id}/edit`}>
                  <Button variant="outline">Editar</Button>
                </Link>
              )}
              {can_delete && (
                <Button variant="ghost" className="text-destructive" onClick={handleDelete}>
                  Eliminar
                </Button>
              )}
            </div>
          </div>
        </div>

        <FichaBody
          analytics={analytics}
          timezone={current_business?.timezone || "America/Mexico_City"}
          metrics={staffMetrics(
            analytics,
            current_business?.timezone || "America/Mexico_City",
          )}
          hrefFor={({ period, pastPage }) => {
            const params = new URLSearchParams();
            params.set("period", period || analytics.period);
            if (pastPage && pastPage > 1) params.set("past_page", String(pastPage));
            return `/${orgSlug}/team/${member.id}?${params.toString()}`;
          }}
        />
      </div>
    </DashboardLayout>
  );
}
